/**
 * Select Component
 * Dropdown selector with validation support
 */

import { forwardRef, useId } from 'react';
import type { SelectProps } from '../types/components';
import { cn } from '../utils/cn';

const sizeStyles: Record<string, string> = {
  sm: 'px-2.5 py-1.5 text-sm',
  md: 'px-3 py-2 text-base',
  lg: 'px-4 py-2.5 text-lg',
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      name,
      label,
      error,
      required,
      hint,
      options,
      value,
      onChange,
      placeholder,
      disabled,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = `select-${generatedId}`;
    const hasError = Boolean(error);

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-300"
          >
            {label}
            {required && <span className="text-gray-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={id}
            name={name}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={disabled}
            required={required}
            className={cn(
              'block w-full rounded border appearance-none bg-white dark:bg-slate-900 dark:text-slate-100',
              'pr-10 transition-colors cursor-pointer',
              'focus:outline-none focus:ring-2 focus:ring-offset-0 dark:focus:ring-slate-400 dark:focus:border-slate-400',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-slate-800 dark:disabled:text-slate-500',
              hasError
                ? 'border-gray-400 focus:border-gray-900 focus:ring-gray-200 dark:border-red-500/70'
                : 'border-gray-300 focus:border-gray-900 focus:ring-gray-100 dark:border-slate-700',
              sizeStyles[size]
            )}
            aria-invalid={hasError}
            aria-describedby={
              hasError ? `${id}-error` : hint ? `${id}-hint` : undefined
            }
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>

          {/* Dropdown arrow */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400 dark:text-slate-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {error && (
          <p id={`${id}-error`} className="mt-1 text-sm text-gray-500 dark:text-red-400" role="alert">
            {error}
          </p>
        )}

        {hint && !error && (
          <p id={`${id}-hint`} className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
